import { useEffect, useMemo, useState } from 'react';
import { TRACKED_REG } from '../../lib/flightTrack';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { TriangleAlert, ChevronRight, Phone, MessageSquare, Mail, Compass } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { computeProfileProgress } from '../../lib/profile';
import { DEMO_AIRCRAFT, DEMO_FLIGHTS, DEMO_UIDS } from '../../lib/demoData';
import { fmtHours, fmtUSD, summarizeCurrentCycle } from '../../lib/billingCycles';
import type { AircraftDoc, FlightDoc } from '../../lib/types';
import { PageHeader } from '../../components/PageHeader';
import { FlightActions } from '../../components/FlightActions';
import { StatusBanner } from '../../components/StatusBanner';
import { showDashboardNotice, coiDueInfo, SHOW_SOLO_NOTICE_TO_PILOTS } from '../../lib/soloGate';
import { WelcomeTour, tourSeen } from '../../components/WelcomeTour';
import { OWNER_FIRST, OWNER_EMAIL, OWNER_PHONE } from '../../lib/identity';

export function LesseeDashboard() {
  const { profile, fbUser, demoMode } = useAuth();
  const { pct, items } = computeProfileProgress(profile);
  const allDone = pct === 100;

  // First-login guided tour for active lessees — auto-opens once per device,
  // replayable from the header button.
  const uid = fbUser?.uid;
  const isActive = profile?.status === 'active';
  const coi = coiDueInfo(profile?.leaseEffectiveDate);

  // Server-stamped so the date is the server's, not the phone's. Clears this
  // banner for good; the booking screen still shows at the moment of decision.
  async function ackSoloNotice() {
    if (demoMode || !db || !fbUser) return;
    await updateDoc(doc(db, 'users', fbUser.uid), { soloNoticeAckAt: serverTimestamp() });
  }
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    if (isActive && uid && !tourSeen(uid)) setShowTour(true);
  }, [isActive, uid]);

  return (
    <>
      {showTour && <WelcomeTour uid={uid} onClose={() => setShowTour(false)} />}
      <PageHeader
        title={`Welcome${
          profile?.personal?.firstName ? `, ${profile.personal.firstName}` : ''
        }`}
        subtitle="Your AJM Aviation lessee portal."
        actions={
          isActive ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setShowTour(true)}
                className="btn-secondary inline-flex items-center gap-1.5 py-1.5 text-sm"
              >
                <Compass size={15} /> Take a tour
              </button>
            </div>
          ) : undefined
        }
      />

      {profile?.status === 'profile_incomplete' && !allDone && (
        <div className="space-y-4">
          <StatusBanner
            tone="warning"
            title={`Profile ${pct}% complete`}
            action={
              <Link to="/profile" className="btn-primary py-1.5 text-sm">
                Complete profile
              </Link>
            }
          >
            Finish your profile and upload your required documents to unlock booking and flight logging.
          </StatusBanner>

          <div className="card p-5">
            <h2 className="font-display text-lg font-semibold text-navy-900">Remaining</h2>
            <ul className="mt-3 space-y-2">
              {items.map((it) => (
                <li key={it.label} className="flex items-center gap-2 text-sm">
                  <span
                    className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${
                      it.done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {it.done ? '✓' : '·'}
                  </span>
                  <span className={it.done ? 'text-ink-muted line-through' : ''}>{it.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {profile?.status === 'verification_pending' && (
        <StatusBanner tone="info" title="Awaiting verification">
          Your profile and documents are with the owner for review. You’ll get an email once you’re
          activated.
        </StatusBanner>
      )}

      {SHOW_SOLO_NOTICE_TO_PILOTS && showDashboardNotice(profile) && (
        <StatusBanner tone="warning" title="Dual instruction only until your insurance certificate is on file">
          Under Section 11.2 of your lease you may fly {TRACKED_REG} <strong>only with a flight instructor named and
          approved on AJM&rsquo;s policy</strong> until AJM has your certificate of insurance. No solo pilot in
          command until then.
          {coi && (
            <>
              {' '}
              {coi.overdue
                ? `It was due ${coi.dueDate}.`
                : `It is due by ${coi.dueDate} (${coi.daysLeft} day${coi.daysLeft === 1 ? '' : 's'}).`}
            </>
          )}{' '}
          Upload it under Documents, or send it to {OWNER_FIRST} and it gets filed for you.
          <div className="mt-3">
            <button type="button" onClick={ackSoloNotice} className="btn-secondary text-sm">
              Got it
            </button>
          </div>
        </StatusBanner>
      )}

      {profile?.status === 'active' && (
        <div className="space-y-4">
          {/* Book and log — the two things you actually come here to do. */}
          <FlightActions bookTo="/book" logTo="/flights/new" />
          <QuickCard icon={TriangleAlert} title="Aircraft squawks" to="/squawks" desc="See current issues & report new." />
          <BillingGlance />
        </div>
      )}

      {profile?.status === 'suspended' && (
        <StatusBanner tone="danger" title="Account suspended">
          Booking and flight logging are blocked. Contact the owner.
        </StatusBanner>
      )}

      <ContactOwner />
    </>
  );
}

// Always-visible way to reach the owner — call, text, or email Casey.

function ContactOwner() {
  const tel = OWNER_PHONE.replace(/[^0-9+]/g, '');
  return (
    <div className="card mt-4 p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-base font-semibold text-navy-900">Need anything? Reach the owner</h2>
          <p className="mt-0.5 text-sm text-ink-muted">{OWNER_FIRST} — AJM Aviation Ventures</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <a
          href={`tel:${tel}`}
          className="flex flex-col items-center gap-1.5 rounded-xl bg-sky-50 px-2 py-3 text-navy-800 transition hover:bg-sky-100"
        >
          <Phone size={20} />
          <span className="text-xs font-medium">Call</span>
        </a>
        <a
          href={`sms:${tel}`}
          className="flex flex-col items-center gap-1.5 rounded-xl bg-sky-50 px-2 py-3 text-navy-800 transition hover:bg-sky-100"
        >
          <MessageSquare size={20} />
          <span className="text-xs font-medium">Text</span>
        </a>
        <a
          href={`mailto:${OWNER_EMAIL}`}
          className="flex flex-col items-center gap-1.5 rounded-xl bg-sky-50 px-2 py-3 text-navy-800 transition hover:bg-sky-100"
        >
          <Mail size={20} />
          <span className="text-xs font-medium">Email</span>
        </a>
      </div>
      <div className="mt-3 text-center text-xs text-ink-muted">
        {OWNER_PHONE} · {OWNER_EMAIL}
      </div>
    </div>
  );
}

// At-a-glance billing for the dashboard: plan, included hours used, this-month total.
function BillingGlance() {
  const { profile, fbUser, demoMode } = useAuth();
  const [flights, setFlights] = useState<FlightDoc[]>([]);
  const [aircraft, setAircraft] = useState<AircraftDoc | null>(null);

  useEffect(() => {
    if (demoMode) {
      const uid = profile?.status === 'active' ? DEMO_UIDS.brian : profile?.uid ?? '';
      setFlights(DEMO_FLIGHTS.filter((f) => f.lesseeUid === uid));
      setAircraft(DEMO_AIRCRAFT[0]);
      return;
    }
    if (!db || !fbUser) return;
    const unsubF = onSnapshot(
      query(collection(db, 'flights'), where('lesseeUid', '==', fbUser.uid)),
      (snap) => setFlights(snap.docs.map((d) => d.data() as FlightDoc))
    );
    const unsubA = onSnapshot(collection(db, 'aircraft'), (snap) =>
      setAircraft((snap.docs[0]?.data() as AircraftDoc) ?? null)
    );
    return () => {
      unsubF();
      unsubA();
    };
  }, [demoMode, fbUser, profile?.status, profile?.uid]);

  const c = useMemo(
    () =>
      aircraft
        ? summarizeCurrentCycle({
            flights,
            aircraft,
            billingDayOfMonth: profile?.billingDayOfMonth ?? 1,
          })
        : null,
    [flights, aircraft, profile?.billingDayOfMonth]
  );
  if (!c) return null;

  // Deliberately calm: lead with hours flown (an accomplishment), not a running
  // dollar total. The actual billing / statements live one tap away on /billing.
  return (
    <Link
      to="/billing"
      className="card group block p-5 transition-all duration-200 hover:shadow-lift hover:-translate-y-0.5"
    >
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-ink-subtle">This month</div>
          <div className="mt-1 font-display text-2xl font-bold tracking-tight text-navy-900">
            {fmtHours(c.hoursFlown)} <span className="text-base font-medium text-ink-muted">Hobbs hours</span>
          </div>
          <div className="mt-0.5 text-sm text-ink-muted">
            {fmtUSD(c.rate)}/hr · no monthly minimum · billed per flight
          </div>
        </div>
        <ChevronRight size={20} className="text-ink-subtle transition-transform group-hover:translate-x-0.5" />
      </div>
      <div className="mt-3 text-xs font-medium text-navy-700">View billing &amp; statements →</div>
    </Link>
  );
}

function QuickCard({ icon: Icon, title, desc, to }: { icon: LucideIcon; title: string; desc: string; to: string }) {
  return (
    <Link
      to={to}
      className="card group flex items-center gap-4 p-5 transition-all duration-200 hover:shadow-lift hover:-translate-y-0.5"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-navy-700">
        <Icon size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-display text-base font-semibold text-navy-900">{title}</div>
        <div className="text-sm text-ink-muted">{desc}</div>
      </div>
      <ChevronRight size={18} className="text-ink-subtle transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}
